/**
 * ARTWORK STUDIO — THE PAGING LOOP (pure, zero imports).
 *
 * A Canva folder answers ONE PAGE at a time and hands back a `continuation`
 * token when there is more. Bezel & Top Designs holds 300+ designs, so a picker
 * that reads only the first response shows the operator a fraction of the
 * folder and gives no sign that the rest exists — which is exactly what was
 * reported. This module follows the token to exhaustion.
 *
 * It is PURE and takes the page fetcher as an ARGUMENT — no `lib/api.js`, so no
 * `import.meta`, so `verify-studio.mjs` can drive the real loop under Node with
 * a stub feed and assert that it keeps requesting until the token is empty.
 *
 * Properties the loop guarantees:
 *   - it requests until `continuation` is falsy (auto-page-all), calling
 *     `onPage` after EVERY page so the grid fills progressively instead of
 *     blocking on the whole folder;
 *   - `PAGE_CAP` bounds it. Hitting the cap is not a silent truncation: the
 *     remaining token is returned so the UI can offer "Load more";
 *   - a repeated continuation token STOPS it (a feed that echoes its own cursor
 *     would otherwise spin forever);
 *   - a failure PART-WAY keeps every page already collected and reports the
 *     reason — losing 250 loaded designs to a flaky page 4 is worse than
 *     showing 250 and saying so;
 *   - ids are deduped across pages, so an overlapping page can never render the
 *     same design twice.
 */

/** How many pages one browse will follow before handing the operator the wheel. */
export const PAGE_CAP = 40

/** A node with nothing in it — the shape every page merges into. */
export function emptyNode(id, name) {
  return { id, name: name || 'Folder', folders: [], items: [], continuation: null, source: null }
}

/**
 * Follow a folder's continuation token to exhaustion.
 *
 * @param {(continuation: string|null) => Promise<object>} fetchPage
 *        Returns one page: `{ id, name, folders, items, continuation, source, reason? }`.
 * @param {{ start?: string|null, maxPages?: number, onPage?: (node) => void,
 *           seed?: object|null }} opts
 *        `start` resumes from a token and `seed` carries the pages already on
 *        screen — together they are the "Load more" path, which must return the
 *        WHOLE folder so far, not just the pages it happened to add.
 * @returns {Promise<object>} the merged node. `continuation` is non-null ONLY
 *        when the loop stopped early and there is genuinely more to fetch.
 */
export async function collectFolder(fetchPage, opts = {}) {
  const { start = null, maxPages = PAGE_CAP, onPage, seed = null } = opts

  const folders = []
  const items = []
  const folderIds = new Set()
  const itemIds = new Set()
  const usedTokens = new Set()

  if (seed) {
    for (const f of seed.folders || []) {
      if (f && f.id && !folderIds.has(f.id)) { folderIds.add(f.id); folders.push(f) }
    }
    for (const it of seed.items || []) {
      const key = it && it.id != null ? String(it.id) : null
      if (key && !itemIds.has(key)) { itemIds.add(key); items.push(it) }
    }
  }

  let cont = start || null
  let pages = 0
  let head = null
  let reason = null
  let partial = false

  for (;;) {
    if (cont) {
      // A feed that hands back the cursor it was just given would loop forever.
      if (usedTokens.has(cont)) { cont = null; break }
      usedTokens.add(cont)
    }

    const page = await fetchPage(cont)
    pages += 1
    if (!head) head = page

    if (page && Array.isArray(page.folders)) {
      for (const f of page.folders) {
        if (!f || !f.id || folderIds.has(f.id)) continue
        folderIds.add(f.id)
        folders.push(f)
      }
    }
    if (page && Array.isArray(page.items)) {
      for (const it of page.items) {
        const key = it && (it.id != null ? String(it.id) : null)
        if (!it || !key || itemIds.has(key)) continue
        itemIds.add(key)
        items.push(it)
      }
    }

    const next = (page && page.continuation) || null

    // A page that came back on the fallback source is the feed failing, not an
    // empty folder. On page 1 that IS the answer; later it is a partial load.
    const degraded = pages > 1 && head && page && page.source && page.source !== head.source
    if (degraded) {
      partial = true
      reason = page.reason || 'The Canva feed stopped answering part-way through this folder.'
    }

    const capped = pages >= maxPages && !!next
    cont = degraded ? null : next

    const snapshot = merged()
    snapshot.continuation = degraded ? null : capped ? next : null
    snapshot.loading = !degraded && !capped && !!next
    if (onPage) onPage(snapshot)

    if (degraded || capped || !next) {
      snapshot.loading = false
      return snapshot
    }
  }

  const out = merged()
  out.continuation = null
  out.loading = false
  return out

  function merged() {
    const base = head || seed || emptyNode(undefined, undefined)
    return {
      id: base.id,
      name: base.name,
      folders,
      items,
      continuation: null,
      source: base.source,
      reason: reason || base.reason || null,
      pages,
      partial,
      loading: false,
    }
  }
}
